import React, { useEffect, useState, useRef, useCallback } from "react";
import { ethers } from "ethers";
import "../../gamble.css";
import tournamentAbi2 from "../../abi/tournament2_abi.json";
import QRCode from "../../QRCodeGenerator.js";

const WSS_RPCS = [
  "wss://api.avax.network/ext/bc/C/ws",
  "wss://avalanche-c-chain-rpc.publicnode.com",
];

const CONTRACTS = {
  V1: {
    address: "0x4f1B0E2AC85a13bCbfFf6B133499dBa0F316E51F",
    label: "7 Step / 128 Players",
    terms: "300",
    sellTerm: "330",
    buyTerm: "1680",
    sellBuyTerm: "1710",
    winners: 4,
    actualBet: 279.0207,
    sellerPrice: 1378.62,
    estimatedPool: 35714.6496,
  },
};

const short1 = (addr) =>
  addr ? `${addr.slice(0, 10)}...${addr.slice(-10)}` : "-";
const short2 = (addr) =>
  addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "-";
const addressLink = (addr) => `https://snowtrace.io/address/${addr}`;

const TokenLogo = ({ size = 12 }) => (
  <img
    src="/logo.svg"
    alt="token logo"
    style={{ width: `${size}px`, verticalAlign: "middle", marginLeft: "2px" }}
  />
);

export default function Gamble2Base() {
  const [data, setData] = useState({
    V1: {
      state: null,
      players: [],
      buyers: [],
      sellers: [],
      previousCycles: [],
    },
  });
  const [price, setPrice] = useState(null);
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(false);
  const [currentQRCodeData, setCurrentQRCodeData] = useState("");
  const [copied, setCopied] = useState(false);

  // ── Claim state ───────────────────────────────────────────────────────────
  const [claimAddress, setClaimAddress] = useState("");
  const [claimableAmount, setClaimableAmount] = useState(null);
  const [claimChecking, setClaimChecking] = useState(false);
  const [claimCheckError, setClaimCheckError] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimTxHash, setClaimTxHash] = useState("");
  const [claimError, setClaimError] = useState("");
  const [claimSuccess, setClaimSuccess] = useState(false);
  // ─────────────────────────────────────────────────────────────────────────

  const providerRef = useRef(null);
  const contractsRef = useRef({});
  const reconnectTimeoutRef = useRef(null);
  const reconnectRefFn = useRef(null);
  const endpointIndex = useRef(0);
  const mountedRef = useRef(true);

  const copy = async (address) => {
    await navigator.clipboard.writeText(address);
    setCopied(address);
    setTimeout(() => setCopied(false), 1200);
  };

  const handleShowQRCode = (contractAddress) => {
    setCurrentQRCodeData(contractAddress);
    setIsQRCodeVisible(true);
  };

  const handleCloseQRCode = () => {
    setIsQRCodeVisible(false);
    setCurrentQRCodeData("");
  };

  // ── Read-only contract helper ─────────────────────────────────────────────
  const getReadContract = useCallback(() => {
    const provider =
      providerRef.current ||
      new ethers.FallbackProvider(
        WSS_RPCS.map((url, i) => ({
          provider: new ethers.JsonRpcProvider(url),
          priority: i,
          weight: i === 0 ? 2 : 1,
          stallTimeout: 2000,
          timeout: 5000,
        })),
      );
    // FIX: was incorrectly referencing CONTRACTS.V3 (doesn't exist); now V1
    return new ethers.Contract(CONTRACTS.V1.address, tournamentAbi2, provider);
  }, []);

  // ── Check claimable balance ───────────────────────────────────────────────
  const handleCheckClaim = async () => {
    setClaimCheckError("");
    setClaimableAmount(null);
    setClaimSuccess(false);
    setClaimTxHash("");
    setClaimError("");

    if (!ethers.isAddress(claimAddress)) {
      setClaimCheckError("Enter a valid wallet address.");
      return;
    }

    setClaimChecking(true);
    try {
      const contract = getReadContract();
      const raw = await contract.claimableBalance(claimAddress);
      setClaimableAmount(ethers.formatEther(raw));
    } catch (e) {
      setClaimCheckError("Failed to read balance. Check your connection.");
    } finally {
      setClaimChecking(false);
    }
  };

  // ── Execute claim ─────────────────────────────────────────────────────────
  const handleClaim = async () => {
    setClaimError("");
    setClaimTxHash("");
    setClaimSuccess(false);

    if (!window.ethereum) {
      setClaimError("No wallet detected. Install MetaMask or Core Wallet.");
      return;
    }

    setClaiming(true);
    try {
      await window.ethereum.request({ method: "eth_requestAccounts" });
      const browserProvider = new ethers.BrowserProvider(window.ethereum);
      const signer = await browserProvider.getSigner();

      const signerAddress = await signer.getAddress();
      if (signerAddress.toLowerCase() !== claimAddress.toLowerCase()) {
        setClaimError(
          `Connected wallet (${short2(signerAddress)}) does not match the checked address (${short2(claimAddress)}). Switch accounts in your wallet.`,
        );
        setClaiming(false);
        return;
      }

      const contract = new ethers.Contract(
        CONTRACTS.V1.address,
        tournamentAbi2,
        signer,
      );

      const tx = await contract.claim();
      setClaimTxHash(tx.hash);
      await tx.wait();
      setClaimSuccess(true);
      setClaimableAmount("0");
    } catch (e) {
      if (e.code === 4001 || e.code === "ACTION_REJECTED") {
        setClaimError("Transaction rejected.");
      } else if (e.message?.includes("nothing claimable")) {
        setClaimError("Nothing to claim for this address.");
      } else {
        setClaimError(e.reason || e.message || "Transaction failed.");
      }
    } finally {
      setClaiming(false);
    }
  };

  // ── Parse logs into cycles ────────────────────────────────────────────────
  const loadCycles = useCallback(async (key, contract, provider) => {
    try {
      const iface = new ethers.Interface(tournamentAbi2);
      const topicRound = iface.getEvent("RoundResolved").topicHash;
      const topicFinished = iface.getEvent("Finished").topicHash;
      const topicBuyer = iface.getEvent("BuyerRegistered").topicHash;
      const topicSeller = iface.getEvent("SellerRegistered").topicHash;

      const latest = await provider.getBlockNumber();
      const START_BLOCK = Math.max(0, latest - 10000);
      const CHUNK = 10;

      let roundRawLogs = [];
      let finishRawLogs = [];
      const buyers = new Set();
      const sellers = new Set();

      for (let from = START_BLOCK; from <= latest; from += CHUNK) {
        const to = Math.min(from + CHUNK - 1, latest);
        try {
          const [rl, fl, bl, sl] = await Promise.all([
            provider.getLogs({
              address: CONTRACTS[key].address,
              fromBlock: from,
              toBlock: to,
              topics: [topicRound],
            }),
            provider.getLogs({
              address: CONTRACTS[key].address,
              fromBlock: from,
              toBlock: to,
              topics: [topicFinished],
            }),
            provider.getLogs({
              address: CONTRACTS[key].address,
              fromBlock: from,
              toBlock: to,
              topics: [topicBuyer],
            }),
            provider.getLogs({
              address: CONTRACTS[key].address,
              fromBlock: from,
              toBlock: to,
              topics: [topicSeller],
            }),
          ]);
          roundRawLogs.push(...rl);
          finishRawLogs.push(...fl);
          bl.forEach((raw) => {
            try {
              buyers.add(iface.parseLog(raw).args.buyer);
            } catch {}
          });
          sl.forEach((raw) => {
            try {
              sellers.add(iface.parseLog(raw).args.seller);
            } catch {}
          });
        } catch {}
      }

      const cycles = {};

      roundRawLogs.forEach((raw) => {
        try {
          const log = iface.parseLog(raw);
          const cycleId = Number(log.args.cycleId);
          if (!cycles[cycleId]) cycles[cycleId] = { rounds: {} };
          cycles[cycleId].rounds[Number(log.args.step)] = {
            winners: [...log.args.winners],
            losers: [...log.args.losers],
          };
        } catch {}
      });

      finishRawLogs.forEach((raw) => {
        try {
          const log = iface.parseLog(raw);
          const cycleId = Number(log.args.cycleId);
          if (!cycles[cycleId]) cycles[cycleId] = { rounds: {} };
          cycles[cycleId].winner = log.args.winner;
          cycles[cycleId].prize = ethers.formatEther(log.args.prize);
        } catch {}
      });

      const parsedCycles = Object.entries(cycles)
        .map(([id, d]) => ({ id: Number(id), ...d }))
        .sort((a, b) => b.id - a.id)
        .slice(0, 5);

      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          previousCycles: parsedCycles,
          buyers: Array.from(buyers),
          sellers: Array.from(sellers),
        },
      }));
    } catch (e) {
      console.warn(`loadCycles ${key}`, e);
    }
  }, []);

  // ── Load live state ───────────────────────────────────────────────────────
  const loadState = useCallback(async (key, contract) => {
    try {
      const [bet, max, count, pool, cycle, stateEnum] = await Promise.all([
        contract.BET(),
        contract.MAX_PLAYERS(),
        contract.playersCount(),
        contract.pool(),
        contract.cycleId(),
        contract.state(),
      ]);

      const playersArr = await Promise.all(
        Array.from({ length: Number(count) }, (_, i) => contract.players(i)),
      );

      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          state: {
            bet: ethers.formatEther(bet),
            max: max.toString(),
            count: count.toString(),
            pool: ethers.formatEther(pool),
            cycleId: cycle.toString(),
            state: Number(stateEnum) === 0 ? "OPEN" : "LOCKED",
          },
          players: playersArr,
        },
      }));
    } catch (e) {
      console.warn(`loadState ${key}`, e);
    }
  }, []);

  // ── Reconnect ─────────────────────────────────────────────────────────────
  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    endpointIndex.current++;
    reconnectTimeoutRef.current = setTimeout(
      () => reconnectRefFn.current?.(),
      3000,
    );
  }, []);

  // ── Connect ───────────────────────────────────────────────────────────────
  const connect = useCallback(async () => {
    try {
      const url = WSS_RPCS[endpointIndex.current % WSS_RPCS.length];
      const provider = new ethers.WebSocketProvider(url);
      await provider.getBlockNumber();

      providerRef.current = provider;

      const contracts = {
        V1: new ethers.Contract(CONTRACTS.V1.address, tournamentAbi2, provider),
      };
      contractsRef.current = contracts;

      await Promise.all(
        Object.entries(contracts).map(([key, contract]) =>
          Promise.all([
            loadState(key, contract),
            loadCycles(key, contract, provider),
          ]),
        ),
      );

      Object.entries(contracts).forEach(([key, contract]) => {
        contract.on("Joined", () => loadState(key, contract));
        contract.on("Finished", () => {
          loadState(key, contract);
          setTimeout(() => loadCycles(key, contract, provider), 5000);
        });
      });

      const ws = provider.websocket;
      if (ws) {
        ws.onclose = reconnect;
        ws.onerror = reconnect;
      }
    } catch (e) {
      reconnect();
    }
  }, [loadState, loadCycles, reconnect]);

  useEffect(() => {
    reconnectRefFn.current = connect;
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    connect();

    const stateInterval = setInterval(() => {
      Object.entries(contractsRef.current).forEach(([key, contract]) =>
        loadState(key, contract),
      );
    }, 10000);

    return () => {
      mountedRef.current = false;
      clearInterval(stateInterval);
      if (reconnectTimeoutRef.current)
        clearTimeout(reconnectTimeoutRef.current);
      try {
        Object.values(contractsRef.current).forEach((c) =>
          c.removeAllListeners(),
        );
        providerRef.current?.destroy?.();
      } catch {}
    };
  }, [connect, loadState]);

  // ── Price ─────────────────────────────────────────────────────────────────
  const loadPrice = useCallback(async () => {
    try {
      const res = await fetch(
        "https://api.dexscreener.com/latest/dex/tokens/0x76938525d4ED7D0b22a899d8bDc1bEF57cd47D18",
      );
      const d = await res.json();
      if (d.pairs && d.pairs.length > 0) setPrice(d.pairs[0].priceUsd);
    } catch (e) {
      console.warn("Price load failed", e);
    }
  }, []);

  useEffect(() => {
    loadPrice();
    const interval = setInterval(loadPrice, 30000);
    return () => clearInterval(interval);
  }, [loadPrice]);

  // ── Claim panel ───────────────────────────────────────────────────────────
  const renderClaimPanel = () => {
    const hasBalance =
      claimableAmount !== null && parseFloat(claimableAmount) > 0;
    const isZero =
      claimableAmount !== null && parseFloat(claimableAmount) === 0;

    return (
      <div className="gamble-content1112">
        <div className="gambleCenteredRowHeader">
          <h5>Claim Your Prize / Refund</h5>
        </div>

        <div className="gamble-content12" style={{ gap: "10px" }}>
          <p style={{ fontSize: "13px", opacity: 0.75, marginBottom: "8px" }}>
            If you won a prize or are owed a refund from the seat trading phase,
            enter your address below to check and withdraw your balance. The
            additional 10% are not being refunded, instead they are being added
            to the prize pool, so the prize for the first place will be min
            17,857.3248 <TokenLogo />, the second place min 7,142.92992{" "}
            <TokenLogo />, the third and fourth places will receive min
            5,357.19744 <TokenLogo /> each.
          </p>

          {/* Address input + check button */}
          <div className="gambleClaimRow">
            <input
              type="text"
              placeholder="0x... your wallet address"
              value={claimAddress}
              onChange={(e) => {
                setClaimAddress(e.target.value.trim());
                setClaimableAmount(null);
                setClaimCheckError("");
                setClaimError("");
                setClaimSuccess(false);
                setClaimTxHash("");
              }}
              className="nameInput"
            />
            <button
              onClick={handleCheckClaim}
              disabled={claimChecking || !claimAddress}
              className="airdropButton"
              style={{
                opacity: claimChecking || !claimAddress ? 0.5 : 1,
              }}
            >
              {claimChecking ? "Checking…" : "Check Balance"}
            </button>
          </div>

          {/* Check error */}
          {claimCheckError && (
            <p style={{ color: "#e74c3c", fontSize: "13px", margin: "4px 0" }}>
              {claimCheckError}
            </p>
          )}

          {/* Balance result card */}
          {claimableAmount !== null && (
            <div
              style={{
                background: hasBalance
                  ? "rgba(46,204,113,0.08)"
                  : "rgba(255,255,255,0.04)",
                border: `1px solid ${hasBalance ? "rgba(46,204,113,0.3)" : "rgba(255,255,255,0.1)"}`,
                borderRadius: "8px",
                padding: "12px 16px",
                marginTop: "6px",
              }}
            >
              <div className="gambleHeaderRow" style={{ marginBottom: "0" }}>
                <div className="gambleRow">
                  <h6>Claimable balance:</h6>
                </div>
                <div className="gettoHeaderRowSub">
                  <h5 style={{ color: hasBalance ? "#2ecc71" : "inherit" }}>
                    {parseFloat(claimableAmount).toFixed(4)}
                    <img
                      src="/logo.svg"
                      alt="token"
                      style={{
                        width: "14px",
                        verticalAlign: "middle",
                        marginLeft: "4px",
                      }}
                    />
                  </h5>
                </div>
              </div>

              {isZero && !claimSuccess && (
                <p style={{ fontSize: "12px", opacity: 0.6, marginTop: "6px" }}>
                  Nothing to claim for this address yet.
                </p>
              )}

              {hasBalance && !claimSuccess && (
                <>
                  <p
                    style={{
                      fontSize: "12px",
                      opacity: 0.65,
                      margin: "8px 0 10px",
                    }}
                  >
                    Connect the wallet that owns this address to withdraw.
                  </p>
                  <button
                    onClick={handleClaim}
                    disabled={claiming}
                    className="sendButton"
                  >
                    {claiming
                      ? "Waiting for wallet…"
                      : `Claim ${parseFloat(claimableAmount).toFixed(4)} tokens`}
                  </button>
                </>
              )}

              {claimTxHash && !claimSuccess && (
                <p style={{ fontSize: "12px", opacity: 0.7, marginTop: "8px" }}>
                  Tx submitted:{" "}
                  <a
                    href={`https://snowtrace.io/tx/${claimTxHash}`}
                    target="_blank"
                    rel="noreferrer"
                    className="address-link"
                    style={{ fontFamily: "monospace" }}
                  >
                    {short2(claimTxHash)}
                  </a>{" "}
                  — confirming…
                </p>
              )}

              {claimSuccess && (
                <div style={{ marginTop: "10px" }}>
                  <p
                    style={{
                      color: "#2ecc71",
                      fontSize: "13px",
                      fontWeight: "600",
                    }}
                  >
                    ✓ Claimed successfully!
                  </p>
                  {claimTxHash && (
                    <a
                      href={`https://basescan.org/tx/${claimTxHash}`}
                      target="_blank"
                      rel="noreferrer"
                      className="address-link"
                      style={{ fontSize: "12px", fontFamily: "monospace" }}
                    >
                      View on Basescan ↗
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {claimError && (
            <p style={{ color: "#e74c3c", fontSize: "13px", marginTop: "6px" }}>
              {claimError}
            </p>
          )}
        </div>
      </div>
    );
  };

  // ── Tournament render ─────────────────────────────────────────────────────
  const renderTournament = (key, config, tournamentData) => {
    const { state, players, buyers, sellers, previousCycles } = tournamentData;
    const EXPLORER = `https://snowtrace.io/address/${config.address}`;
    const primaryBetUsd = price
      ? `$${(Number(config.terms) * Number(price)).toFixed(6)}`
      : "Loading...";
    const poolUsd = price
      ? (config.estimatedPool * Number(price)).toFixed(6)
      : null;

    return (
      <div className="gamble-wrapperGlobal">
        <div className="gamble-wrapper">
          {/* ── BLOCK 1: Header / contract info ── */}
          <div className="gamble-content1">
            <div className="gambleRow">
              <h5>{config.label}</h5>
            </div>
            <div className="gamble-content12">
              <h5>
                {config.terms}
                <TokenLogo size={15} /> = {primaryBetUsd}
              </h5>
              <div className="gambleRow">
                <a
                  href={EXPLORER}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="address-link"
                >
                  <div className="gambleRow">{config.address}</div>
                </a>
              </div>
              <div className="headerButtonRow">
                {copied === config.address && (
                  <span className="copy-msg-inline">Copied</span>
                )}
                <button
                  onClick={() => copy(config.address)}
                  className="copyButton"
                  title="Copy address"
                >
                  📋
                </button>
                <button
                  onClick={() => handleShowQRCode(config.address)}
                  className="copyButton"
                >
                  🔳
                </button>
              </div>
              {isQRCodeVisible && currentQRCodeData === config.address && (
                <div className="qr-code-modal">
                  <div className="qr-code-container">
                    <h6 style={{ color: "black" }}>{config.address}</h6>
                    <div className="gettoHeaderRowSub">
                      <p>
                        To join the tournament send {config.terms} <TokenLogo />{" "}
                        to the contract address.
                      </p>
                    </div>
                    <QRCode
                      address={config.address}
                      id={`qr-${config.address}`}
                    />
                    <button onClick={handleCloseQRCode}>Close</button>
                  </div>
                </div>
              )}
            </div>

            <div className="gamble-content11">
              {state ? (
                <>
                  {[
                    ["Cycle:", state.cycleId],
                    ["Winners:", config.winners],
                    ["Players:", `${state.count} / ${state.max}`],
                  ].map(([label, val]) => (
                    <div className="gambleHeaderRow" key={label}>
                      <div className="gambleRow">
                        <h6>{label}</h6>
                      </div>
                      <div className="gettoHeaderRowSub">
                        <h5>{val}</h5>
                      </div>
                    </div>
                  ))}
                  <div className="gambleHeaderRow">
                    <div className="gambleRow">
                      <h6>to JOIN:</h6>
                    </div>
                    <div className="gettoHeaderRowSub">
                      <h5>
                        send {config.terms}
                        <TokenLogo size={16} />
                      </h5>
                    </div>
                  </div>

                  <div className="gambleHeaderRow">
                    <div className="gambleRow">
                      <h6>To join & sell:</h6>
                    </div>
                    <div className="gettoHeaderRowSub">
                      <h5>
                        send {config.sellTerm}
                        <TokenLogo size={16} />
                      </h5>
                    </div>
                  </div>
                  <div className="gambleHeaderRow">
                    <div className="gambleRow">
                      <h6>To join & buy:</h6>
                    </div>
                    <div className="gettoHeaderRowSub">
                      <h5>
                        send {config.buyTerm}
                        <TokenLogo size={16} />
                      </h5>
                    </div>
                  </div>
                  <div className="gambleHeaderRow">
                    <div className="gambleRow">
                      <h6>To join & buy/sell:</h6>
                    </div>
                    <div className="gettoHeaderRowSub">
                      <h5>
                        send {config.sellBuyTerm}
                        <TokenLogo size={16} />
                      </h5>
                    </div>
                  </div>
                  <div className="gambleHeaderRow">
                    <div className="gambleRow">
                      <h6>Seat price:</h6>
                    </div>
                    <div className="gettoHeaderRowSub">
                      <h5>
                        {config.sellerPrice}
                        <TokenLogo size={16} />
                      </h5>
                    </div>
                  </div>
                  <div className="gambleHeaderRow">
                    <div className="gambleRow">
                      <h6>Actual Bet:</h6>
                    </div>
                    <div className="gettoHeaderRowSub">
                      <h5>
                        {config.actualBet}
                        <TokenLogo size={16} />
                      </h5>
                    </div>
                  </div>

                  <div className="gambleHeaderRow">
                    <div className="gambleRow">
                      <h6>Pool:</h6>
                    </div>
                    <div className="gettoHeaderRowSub">
                      <h5
                        title={
                          poolUsd
                            ? `Estimated Pool USD Value: $${poolUsd}`
                            : "Loading USD value..."
                        }
                        style={{ cursor: "help" }}
                      >
                        {state.pool} / {config.estimatedPool}
                        <TokenLogo size={16} />
                      </h5>
                    </div>
                  </div>
                </>
              ) : (
                <p>Loading...</p>
              )}
            </div>
          </div>

          {/* ── BLOCK 2: Current Players ── */}
          <div className="gamble-content1112">
            <div className="gambleCenteredRowHeader">
              <h5>Current Players</h5>
            </div>
            <div className="gamble-content123">
              <div className="gambleList">
                {players.length === 0 && <p>No players yet</p>}
                {players.map((p, i) => {
                  const isBuyer = buyers.includes(p);
                  const isSeller = sellers.includes(p);
                  let color = "#ccc";
                  let label = "Regular";
                  if (isBuyer && isSeller) {
                    color = "gold";
                    label = "Buyer/Seller";
                  } else if (isBuyer) {
                    color = "#2ecc71";
                    label = "Buyer";
                  } else if (isSeller) {
                    color = "#e74c3c";
                    label = "Seller";
                  }

                  return (
                    <div className="gambleRow" key={p}>
                      <p style={{ color }}>
                        {i + 1}:{" "}
                        <a
                          href={addressLink(p)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="address-link"
                        >
                          {short1(p)}
                        </a>{" "}
                        <span style={{ opacity: 0.7, marginLeft: 6 }}>
                          ({label})
                        </span>
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── BLOCK 3: Previous Cycles ── */}
          <div className="gamble-content1112">
            <div className="gambleCenteredRowHeader">
              <h5>Previous Cycles</h5>
            </div>
            <div className="gamble-content111">
              {previousCycles.length === 0 && (
                <div className="gamblePlaceholder">
                  <p>No previous cycles found</p>
                </div>
              )}
              {previousCycles.map((cycle) => {
                const sortedRounds = Object.entries(cycle.rounds || {}).sort(
                  (a, b) => Number(a[0]) - Number(b[0]),
                );

                // Derive 4 winners from round data (same logic as Gamble2)
                const semifinal =
                  sortedRounds.length >= 2
                    ? sortedRounds[sortedRounds.length - 2][1]
                    : null;
                const finalRound =
                  sortedRounds.length >= 1
                    ? sortedRounds[sortedRounds.length - 1][1]
                    : null;

                const champion =
                  cycle.winner ||
                  (finalRound?.winners?.length ? finalRound.winners[0] : null);
                const secondPlace = finalRound?.losers?.length
                  ? finalRound.losers[0]
                  : null;
                const thirdPlace = semifinal?.losers?.[0] || null;
                const fourthPlace = semifinal?.losers?.[1] || null;

                return (
                  <div
                    key={cycle.id}
                    className="gambleBlock"
                    style={{ marginBottom: "25px" }}
                  >
                    <div className="gambleCenteredRow">
                      <h5>CYCLE #{cycle.id}</h5>
                    </div>

                    {[
                      [champion, "1st Place", "50%"],
                      [secondPlace, "2nd Place", "20%"],
                      [thirdPlace, "3rd Place", "15%"],
                      [fourthPlace, "4th Place", "15%"],
                    ].map(([addr, label, pct]) =>
                      addr ? (
                        <React.Fragment key={label}>
                          <div className="gambleHeaderRow">
                            <div className="gambleRow">
                              <h5>{label}:</h5>
                            </div>
                            <div className="gettoHeaderRowSub">
                              <a
                                href={addressLink(addr)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="address-link"
                              >
                                {short2(addr)}
                              </a>
                            </div>
                          </div>
                          <div className="gambleHeaderRow">
                            <div className="gambleRow">
                              <h5>{label} Prize:</h5>
                            </div>
                            <div className="gettoHeaderRowSub">
                              <p>
                                {pct} Pool <TokenLogo size={16} />
                              </p>
                            </div>
                          </div>
                        </React.Fragment>
                      ) : null,
                    )}

                    {sortedRounds.map(([step, round]) => (
                      <div key={`${cycle.id}-${step}`}>
                        <div className="gambleRow">
                          <h5>Step {step}</h5>
                        </div>
                        {round.winners.map((w, i) => {
                          const l = round.losers[i];
                          return (
                            <div
                              className="gambleRow"
                              key={`${cycle.id}-${step}-${i}`}
                            >
                              <p>
                                <a
                                  href={addressLink(w)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="address-link"
                                >
                                  {short2(w)}
                                </a>{" "}
                                (W) -{" "}
                                <a
                                  href={addressLink(l)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="address-link"
                                >
                                  {short2(l)}
                                </a>{" "}
                                (L)
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="global-contentClaim">
          {/* ── BLOCK 4: Claim panel ── */}
          {renderClaimPanel()}
        </div>
      </div>
    );
  };

  return (
    <div className="gamble-container">
      <div className="about-block">
        <div className="about-block" style={{ marginBottom: "30px" }}>
          <div className="global-content2">
            <h5>
              7 STEP / 128 PLAYERS TOURNAMENT WITH SEAT BUY/SELL FUNCTIONALITY
              AND 4 WINNERS
            </h5>
            <p>
              The 7 step tournament consists of 128 players. To take part in
              tournament each player sends the exact bet amount to the smart
              contracts address. To take part in tournament each player sends
              the exact bet amount to the smart contracts address. Then 6.9%
              will be deducted to cover costs and ensure a profit, the rest will
              be added to the pot, and the players address will be automatically
              added to the players list. Whenever the last player joins, the pot
              will become ((BET - 0.1%) - 6.9%) * 128. Then it will
              automatically determine the winner by getting random numbers from
              Chainlink VRF, shuffling the players list. In this case the
              contract doesn't send automatically the prizes. You must claim
              your prize manually. The first place address will receive 50% of
              the pot, second place will receive 20%, the third and fourth
              places will receive by 15%. This tournament includes a
              seat-trading feature. Before joining, you can decide whether you
              want to participate in trading for the Step 3 seats or simply join
              as a regular player without any trading activity. Each option
              requires sending a specific predefined amount of tokens when
              joining the tournament. The amount you send determines which
              functionality is activated for your participation.
            </p>

            <div className="gamble-wrapper7">
              <div className="gamble22-content1">
                <div className="gamble2-content1">
                  <table className="terms-table">
                    <thead></thead>

                    <tbody>
                      <tr>
                        <td>128→64</td>
                        <td>elimination</td>
                      </tr>

                      <tr>
                        <td>64→32</td>
                        <td>elimination</td>
                      </tr>

                      <tr>
                        <td>32→16</td>
                        <td>seats become tradable</td>
                      </tr>

                      <tr>
                        <td>MARKET PHASE</td>
                        <td>eliminated buyers buy surviving sellers seats</td>
                      </tr>

                      <tr>
                        <td>16→8</td>
                        <td>elimination</td>
                      </tr>

                      <tr>
                        <td>8→4</td>
                        <td>elimination</td>
                      </tr>

                      <tr>
                        <td>4→2</td>
                        <td>semifinal</td>
                      </tr>

                      <tr>
                        <td>2→1</td>
                        <td>final</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="gamble22-content12">
                  <h4>The Prize Pool: </h4>
                  <h5>1st Place Receives 50% of the prize pool</h5>
                  <h5>2nd Place Receives 20% of the prize pool</h5>
                  <h5>3rd Place Receives 15% of the prize pool</h5>
                  <h5>4th Place Receives 15% of the prize pool</h5>
                </div>
              </div>
              <div className="gamble2-content12">
                {/* EXISTING TEXT */}

                <h4> Regular join</h4>
                <p>
                  Just send bet amount to the contracts address. In this case
                  you don't buy/sell seats, if you are one of the 4 finalists
                  you receive your prize.
                </p>
                <h4> Join with sell Strategy</h4>
                <p>
                  Send bet amount + 10%. In this case you literally say: "If I
                  become one of the third steps 16 winners and if there will be
                  a buyer, exclude me from the tournament, instead of my address
                  put his address and when the tournament resolved, I'll
                  withdraw my payment." The additional 10% that sellers send to
                  trigger the sell function is not being refunded if the trade
                  doesn't trigger, instead that amounts are added to the prize
                  pool.
                </p>
                <h4>Join with buy Strategy</h4>
                <p>
                  Send bet amount + 460%. Here you say: "If I don't become one
                  of the third steps 16 winners (if I lose before that) and if
                  there will be a seller among the third steps 16 winners,
                  exclude him from the tournament, instead of his address put my
                  address and when tournament resolved send him my payment for
                  the seat, but if I will become one of the third steps 16
                  winners, just keep my tokens and when the tournament resolved
                  I will withdraw them manually."
                </p>
                <h4>Join with buy/sell Strategy</h4>
                <p>
                  Send bet amount + 10% + 460%. This mode allows users to
                  predefine both buy and sell functions. You say: "If I do not
                  become one of the 16 winners after the third elimination step,
                  automatically buy a surviving seat. If I do become one of the
                  16 winners after the third elimination step, automatically
                  sell my seat if there is a buyer." In this case again if buy
                  function is not triggered the seat price will be refunded, but
                  the 10% that players send to trigger the sell function, are
                  being added to the prize pot.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderTournament("V1", CONTRACTS.V1, data.V1)}
    </div>
  );
}
